import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  geoOrthographic,
  geoPath,
  geoGraticule10,
  geoDistance,
  geoInterpolate,
} from 'd3';

// Home base + a curated set of places — travel routes + photo pins.
const HOME = { name: 'New York', coords: [-74.0, 40.7] };
const PLACES = [
  { name: 'Mumbai', coords: [72.8, 19.1] },
  { name: 'London', coords: [-0.13, 51.5] },
  { name: 'Punta Cana', coords: [-68.4, 18.6] },
  { name: 'Los Cabos', coords: [-109.9, 22.9] },
  { name: 'Cancun', coords: [-86.9, 21.2] },
  { name: 'Miami', coords: [-80.2, 25.8] },
  { name: 'Sydney', coords: [151.2, -33.9] },
  { name: 'Melbourne', coords: [144.9, -37.8] },
  // Represents San Francisco + Los Angeles as one state-level pin.
  { name: 'California', coords: [-120.3, 36.0] },
  { name: 'Bahamas', coords: [-77.4, 25.0] },
  { name: 'Hawaii', coords: [-157.8, 21.3] },
  // Represents Toronto + Calgary + Whistler as one country-level pin.
  { name: 'Canada', coords: [-105.5, 48.3] },
];

const SIZE = 552; // 460 * 1.2 — globe made 20% larger
const R = SIZE / 2 - 8;

const prefersReduced = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const DEFAULT_LABEL = 'Travel · Photography · Cloud';
const DEFAULT_HEADING = "Places I've\nmoved through.";
const DEFAULT_SUB = "A spinning record of flights chased, cities shot on film, and the " +
  'sky in between. Grab it — give the planet a spin.';

export default function GlobeSection({ content }) {
  const [rotation, setRotation] = useState([-30, -18]);
  const rotRef = useRef(rotation);
  rotRef.current = rotation;
  const dragRef = useRef(null);
  const tickRef = useRef(0);
  const [t, setT] = useState(0); // animation clock for the travelling pulses

  // Build the projection for the current rotation.
  const projection = useMemo(
    () =>
      geoOrthographic()
        .scale(R)
        .translate([SIZE / 2, SIZE / 2])
        .rotate([rotation[0], rotation[1], 0])
        .clipAngle(90),
    [rotation]
  );

  const path = useMemo(() => geoPath(projection), [projection]);
  const graticule = useMemo(() => geoGraticule10(), []);

  // Static great-circle arcs from home → each place (sampled points).
  const arcs = useMemo(
    () =>
      PLACES.map((p) => {
        const interp = geoInterpolate(HOME.coords, p.coords);
        const pts = Array.from({ length: 48 }, (_, i) => interp(i / 47));
        return { to: p, line: { type: 'LineString', coordinates: pts }, interp };
      }),
    []
  );

  // Auto-rotate + advance the pulse clock unless the user is dragging.
  useEffect(() => {
    if (prefersReduced()) return undefined;
    let raf;
    let last = 0;
    const loop = (ts) => {
      if (!last) last = ts;
      const dt = ts - last;
      last = ts;
      if (!dragRef.current) {
        const [l, p] = rotRef.current;
        setRotation([l + dt * 0.012, p]);
      }
      tickRef.current += dt;
      // Drive the pulse clock at ~20fps to keep re-renders cheap.
      setT(tickRef.current);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  // A point is on the visible (near) hemisphere when its angular distance from
  // the projection centre is < 90°.
  const center = [-rotation[0], -rotation[1]];
  const visible = (coords) => geoDistance(coords, center) < Math.PI / 2 - 0.02;

  const project = (coords) => projection(coords);

  // Pulse travels 0→1 along each arc on a staggered loop.
  const pulsePos = (arc, i) => {
    const period = 4200;
    const frac = (((t + i * 520) % period) / period);
    const pt = arc.interp(frac);
    return { pt, frac, vis: visible(pt) };
  };

  // ── Drag handling (pointer) ──
  const onPointerDown = (e) => {
    dragRef.current = { x: e.clientX, y: e.clientY, rot: rotRef.current };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const k = 0.4;
    const lambda = d.rot[0] + (e.clientX - d.x) * k;
    const phi = Math.max(-85, Math.min(85, d.rot[1] - (e.clientY - d.y) * k));
    setRotation([lambda, phi]);
  };
  const onPointerUp = (e) => {
    dragRef.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  const sphere = { type: 'Sphere' };

  const label = content?.label ?? DEFAULT_LABEL;
  const heading = content?.heading ?? DEFAULT_HEADING;
  const headingLines = heading.split('\n');
  const sub = content?.sub ?? DEFAULT_SUB;

  return (
    <section className="globe-section" id="globe">
      <div className="globe-aura" aria-hidden="true">
        <span className="globe-orb globe-orb-1" />
        <span className="globe-orb globe-orb-2" />
      </div>

      <div className="globe-inner">
        <div className="globe-copy">
          <span className="section-label">{label}</span>
          <h2 className="globe-heading">
            {headingLines.map((line, i) => (
              <React.Fragment key={i}>
                {i > 0 ? <span className="globe-heading-accent">{line}</span> : line}
                {i === 0 && <br />}
              </React.Fragment>
            ))}
          </h2>
          <p className="globe-sub">{sub}</p>
          <div className="globe-stats">
            <div className="globe-stat">
              <span className="globe-stat-num">{PLACES.length + 1}</span>
              <span className="globe-stat-label">Cities</span>
            </div>
            <div className="globe-stat">
              <span className="globe-stat-num">4</span>
              <span className="globe-stat-label">Continents</span>
            </div>
            <div className="globe-stat">
              <span className="globe-stat-num">∞</span>
              <span className="globe-stat-label">Frames</span>
            </div>
          </div>
        </div>

        <div className="globe-stage">
          <svg
            className="globe-svg"
            viewBox={`0 0 ${SIZE} ${SIZE}`}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerUp}
            role="img"
            aria-label="Interactive 3D globe of places visited"
          >
            <defs>
              <radialGradient id="globe-fill" cx="38%" cy="34%" r="78%">
                <stop offset="0%" stopColor="var(--bg-3)" />
                <stop offset="70%" stopColor="var(--bg-2)" />
                <stop offset="100%" stopColor="var(--bg)" />
              </radialGradient>
              <radialGradient id="globe-atmo" cx="50%" cy="50%" r="50%">
                <stop offset="82%" stopColor="transparent" />
                <stop offset="96%" stopColor="var(--green-glow)" />
                <stop offset="100%" stopColor="transparent" />
              </radialGradient>
            </defs>

            {/* Atmosphere halo */}
            <circle cx={SIZE / 2} cy={SIZE / 2} r={R + 6} fill="url(#globe-atmo)" />

            {/* Ocean sphere */}
            <path d={path(sphere)} fill="url(#globe-fill)" stroke="var(--border)" strokeWidth="1" />

            {/* Graticule wireframe */}
            <path
              className="globe-graticule"
              d={path(graticule)}
              fill="none"
              stroke="var(--border)"
              strokeWidth="0.5"
            />

            {/* Travel arcs */}
            {arcs.map((arc, i) => (
              <path
                key={`arc-${i}`}
                className="globe-arc"
                d={path(arc.line) || undefined}
                fill="none"
                stroke="var(--green)"
                strokeWidth="1.2"
                strokeLinecap="round"
                style={{ animationDelay: `${i * 0.18}s` }}
              />
            ))}

            {/* Travelling pulses along each arc */}
            {arcs.map((arc, i) => {
              const { pt, vis } = pulsePos(arc, i);
              if (!vis) return null;
              const [x, y] = project(pt);
              return (
                <circle key={`pulse-${i}`} cx={x} cy={y} r="2.6" className="globe-pulse" />
              );
            })}

            {/* Home base */}
            {visible(HOME.coords) && (() => {
              const [x, y] = project(HOME.coords);
              return (
                <g key="home">
                  <circle cx={x} cy={y} r="9" className="globe-home-ring" />
                  <circle cx={x} cy={y} r="4" className="globe-home" />
                </g>
              );
            })()}

            {/* Place pins */}
            {PLACES.map((p) => {
              if (!visible(p.coords)) return null;
              const [x, y] = project(p.coords);
              return (
                <g key={p.name} className="globe-pin">
                  <circle cx={x} cy={y} r="3" />
                  <text x={x + 7} y={y + 3}>{p.name}</text>
                </g>
              );
            })}
          </svg>

          <span className="globe-hint">drag to spin ↺</span>
        </div>
      </div>
    </section>
  );
}
